<template>
    <article class="like-a-local">
        <div class="content">
            <div class="copy">
                <!-- Name and type share a row, type pushed right and aligned to the
                     top of the name, which wraps underneath it rather than being
                     squeezed (Asana O2-14). The type comes second in the markup so
                     it reads after the name for screen readers and tab order; the
                     visual order is the same either way. -->
                <div class="title-row">
                    <NuxtLink :to="website" class="link" external target="_blank">
                        <h3 v-html="siteName" class="headline-2"></h3>
                    </NuxtLink>

                    <p v-html="type" class="gray-tag" v-if="type"></p>
                </div>

                <p v-html="location" class="address body-book"></p>

                <p v-html="description" class="description body-book" v-if="description"></p>

                <p class="recommended" v-if="recommendedBy">Recommended by <span v-html="recommendedBy"></span></p>
            </div>
        </div>
    </article>
</template>

<script setup>
    const props = defineProps({
        item: {
            type: Object,
            required: true,
        },
    });

    const { item } = toRefs(props);

    const location = computed(() => {
        const address = item.value?.fields?.['Address or Coordinates'] || '';
        if (address.length) {
            let index;
            if (address.includes(', Chicago')) {
                index = address.indexOf(', Chicago');
            } else if (address.includes('Chicago, IL')) {
                index = address.indexOf('Chicago, IL');
            } else if (address.includes('Chicago. IL')) {
                index = address.indexOf('Chicago. IL');
            }
            return address.slice(0, index);
        }
        return address;
    });

    const website = item.value?.fields?.['Website'] || ' ';
    const siteName = item.value?.fields?.['Place Name'] || ' ';
    const recommendedBy = item.value?.fields?.['Recommended By'] || '';
    const type = item.value?.fields?.['Type'] || '';
    // Editors had been filling this in in Airtable and seeing nothing, because
    // the card never rendered it (Asana O2-31). 36 of the 161 points of interest
    // carry one; the rest simply skip the paragraph.
    const description = item.value?.fields?.['Description'] || '';
</script>

<style lang="scss" scoped>
    .like-a-local {
        display: flex;
        padding: 15px;
        border: 1px solid $turquoise;
        // Anchors the stretched link below.
        position: relative;

        @media (min-width: $tablet) {
            height: 100%;
        }

        // The card is a flex container and .content never grew, so it only ever
        // measured as wide as its longest line — 232px inside a 374px card. That
        // went unnoticed while the type sat on its own line; now that the type is
        // pushed to the right it has to be the card's right edge, not the text's.
        .content {
            flex: 1 1 auto;
            min-width: 0;
        }

        .title-row {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-bottom: 5px;

            .link {
                // Takes the slack so the type keeps its natural width, and
                // min-width:0 lets a long name actually wrap inside a flex item
                // instead of forcing the row wider.
                flex: 1 1 auto;
                min-width: 0;
                margin-bottom: 0;
            }
        }

        .gray-tag {
            background-color: $turquoise-light;
            margin-top: 0;
            flex: 0 0 auto;
        }

        .link {
            display: flex;
            margin-bottom: 5px;

            // The whole card is the click target, not just the name. Done with
            // a stretched pseudo-element rather than by wrapping the card in an
            // anchor: the card sits inside a carousel item that handles its own
            // clicks, and nesting anchors would be invalid besides. Text stays
            // selectable and the link keeps its own focus ring.
            &::after {
                content: '';
                position: absolute;
                inset: 0;
            }
        }

        .address {
            margin-bottom: 10px;
        }

        .description {
            margin-bottom: 10px;

            // Sits directly under the address, which is also body-book (350), so
            // the two ran together as one block. 500 is Avenir 65 Medium — a
            // weight we actually load, so it is a real face rather than a
            // synthesised bold. 800 (body-heavy) was far too strong for body copy.
            font-weight: 500;

            // The cards are equal-height at tablet+, so one long description
            // drags every card in the row up with it — measured at this width, a
            // 957-character entry (the longest in the data) took the whole row
            // from 158px to 620px, including cards with no description at all.
            //
            // Three lines rather than the six this originally shipped with, at
            // the client's request (Asana O2-70). That trims the median entry
            // (355 characters) too, not just the long tail, so more cards now end
            // in an ellipsis than before — which is the intent: the card is a
            // teaser and the full text lives behind the link.
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            line-clamp: 3;
            overflow: hidden;
        }

        .recommended {
            font-style: italic;
        }
    }

    @media (min-width: $tablet) {
        .active-item .like-a-local {
            border: none;
        }
    }
</style>
